import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react';
import type { Key, KeyboardEvent, ReactElement, ReactNode, RefAttributes } from 'react';
import { flushSync } from 'react-dom';
import clsx from 'clsx';

import {
  initialSelectedPosition,
  RowSelectionChangeProvider,
  RowSelectionProvider,
  useCalculatedColumns,
  useColumnWidths,
  useGridDimensions,
  useLatestFunc,
  usePresenceIndex,
  useRangeSelection,
  useScrollState,
  useScrollToPosition,
  useSelectedPosition,
  useViewportColumns,
  useViewportRows,
  type PartialPosition,
  type SelectCellState,
} from './hooks';
import {
  assertIsValidKeyGetter,
  canExitGrid,
  createCellEvent,
  focusCellOrCellContent,
  getCellToScroll,
  getColSpan,
  getNextSelectedCellPosition,
  isCtrlKeyHeldDown,
  isDefaultCellInput,
  isInFloatingLayer,
  isSelectedCellEditable,
  renderMeasuringCells,
  resolveCopiedRange,
  scrollIntoView,
  sign,
} from './utils';
import {
  gridTooltipId,
  type CalculatedColumn,
  type CellClickArgs,
  type CellKeyboardEvent,
  type CellKeyDownArgs,
  type CellMouseEvent,
  type CellNavigationMode,
  type CellRangeSelection,
  type CellSelectArgs,
  type Column,
  type ColumnOrColumnGroup,
  type CopiedOutline,
  type FillEvent,
  type Position,
  type PresenceMarker,
  type RenderRowProps,
  type RowsChangeData,
  type SelectRowEvent,
  type SortColumn,
} from './types';
import DragHandle from './DragHandle';
import EditCell from './EditCell';
import GroupedColumnHeaderRow from './GroupedColumnHeaderRow';
import HeaderRow from './HeaderRow';
import { defaultRenderRow } from './Row';
import {
  frozenColumnShadowClassname,
  frozenColumnShadowTopClassname,
  rangeSelectingClassname,
  rootClassname,
  tooltipContainerClassname,
  tooltipSwatchClassname,
  viewportDraggingClassname,
} from './style/core.css';
import SummaryRow from './SummaryRow';
import { Tooltip } from 'react-tooltip';

export type { SelectCellState };

type DefaultColumnOptions<R, SR> = Pick<
  Column<R, SR>,
  'width' | 'minWidth' | 'maxWidth' | 'resizable' | 'sortable' | 'draggable'
>;

export interface DataGridHandle {
  element: HTMLDivElement | null;
  scrollToCell: (position: PartialPosition) => void;
  selectCell(position: null): void;
  selectCell(position: Position, enableEditor?: boolean, scrollToCell?: boolean): void;
  /** Collapse an active range selection back to its anchor cell. */
  collapseRange: () => void;
}

type SharedDivProps = Pick<
  React.HTMLAttributes<HTMLDivElement>,
  'role' | 'aria-label' | 'aria-labelledby' | 'aria-describedby' | 'aria-rowcount' | 'className' | 'style' | 'onScroll'
>;

export interface DataGridProps<R, SR = unknown, K extends Key = Key> extends SharedDivProps {
  /**
   * Grid and data Props
   */
  /** An array of objects representing each column on the grid */
  columns: readonly ColumnOrColumnGroup<NoInfer<R>, NoInfer<SR>>[];
  /** A function called for each rendered row that should return a plain key/value pair object */
  rows: readonly R[];
  /**
   * Rows to be pinned at the top of the rows view for summary, the vertical scroll bar will not scroll these rows.
   */
  topSummaryRows?: readonly SR[];
  /**
   * Rows to be pinned at the bottom of the rows view for summary, the vertical scroll bar will not scroll these rows.
   */
  bottomSummaryRows?: readonly SR[];
  /** The getter should return a unique key for each row */
  rowKeyGetter?: (row: NoInfer<R>) => K;
  onRowsChange?: (rows: NoInfer<R>[], data: RowsChangeData<NoInfer<R>, NoInfer<SR>>) => void;

  /**
   * Dimensions props
   */
  /**
   * The height of each row in pixels
   * @default 35
   */
  rowHeight?: number | ((row: NoInfer<R>) => number);
  /**
   * The height of the header row in pixels
   * @default 35
   */
  headerRowHeight?: number;
  /**
   * The height of each summary row in pixels
   * @default 35
   */
  summaryRowHeight?: number;

  /**
   * Feature props
   */
  /** Set of selected row keys */
  selectedRows?: ReadonlySet<K>;
  /** Function called whenever row selection is changed */
  onSelectedRowsChange?: (selectedRows: Set<NoInfer<K>>) => void;
  /** Used for multi column sorting */
  sortColumns?: readonly SortColumn[];
  onSortColumnsChange?: (sortColumns: SortColumn[]) => void;
  defaultColumnOptions?: DefaultColumnOptions<NoInfer<R>, NoInfer<SR>>;
  /**
   * Concurrent-editing markers drawn as an outline on individual cells, addressed
   * by `{ rowKey, columnKey }`. Requires `rowKeyGetter`. Keep the array
   * referentially stable so presence updates only re-render affected cells.
   */
  presenceMarkers?: readonly PresenceMarker[];
  onFill?: (event: FillEvent<NoInfer<R>>) => NoInfer<R>;
  /**
   * Copied-range (marching-ants) highlight, addressed by data keys like
   * `presenceMarkers` (requires `rowKeyGetter`). Ownership lives with the
   * consumer (see `useCopiedOutline`); the grid resolves the keys against its
   * current row/column order and draws the outline only while they still form
   * a contiguous rectangle — a re-sort or filter that breaks contiguity makes
   * it silently disappear.
   */
  copiedOutline?: CopiedOutline | null;
  /**
   * Fired on the native `copy` event when a plain cell selection is active. The
   * grid prevents the default and hands over the selected rectangle together
   * with its resolved rows and leaf columns (both in visual order, so
   * `rows[i]`/`columns[j]` is the cell at `startRowIdx + i` / `startIdx + j`);
   * the consumer populates `event.clipboardData` flavors (see
   * `writeGridClipboard`).
   */
  onClipboardCopy?: (
    args: {
      range: CellRangeSelection;
      rows: readonly NoInfer<R>[];
      columns: readonly CalculatedColumn<NoInfer<R>, NoInfer<SR>>[];
    },
    event: React.ClipboardEvent<HTMLDivElement>,
  ) => void;
  /**
   * Fired on the native `paste` event outside edit mode. The consumer reads
   * `event.clipboardData` (see `readGridClipboard`) and owns the mutation; the
   * target rectangle is anchored at `target` — the active cell — and sized
   * from the payload, which only the consumer knows. `rows` and leaf `columns`
   * (both in visual order, indexable by `target.rowIdx` / `target.idx`)
   * resolve it.
   *
   * May return the rectangle it wrote (with `target` as the top-left corner);
   * the grid then moves the range selection over it, Excel-style. Return nothing
   * for a no-op or single-cell paste to leave the selection untouched.
   */
  onClipboardPaste?: (
    args: {
      target: Position;
      rows: readonly NoInfer<R>[];
      columns: readonly CalculatedColumn<NoInfer<R>, NoInfer<SR>>[];
    },
    event: React.ClipboardEvent<HTMLDivElement>,
  ) => CellRangeSelection | void;
  /**
   * Fired on the native `cut` event under the same conditions and with the
   * same arguments as `onClipboardCopy`. The consumer populates the clipboard
   * flavors and owns clearing the cut rectangle's cells.
   */
  onClipboardCut?: (
    args: {
      range: CellRangeSelection;
      rows: readonly NoInfer<R>[];
      columns: readonly CalculatedColumn<NoInfer<R>, NoInfer<SR>>[];
    },
    event: React.ClipboardEvent<HTMLDivElement>,
  ) => void;
  /** Fired when the user dismisses the copied-range highlight (Escape). */
  onCopiedOutlineClear?: () => void;

  /**
   * Event props
   */
  /** Function called whenever a cell is clicked */
  onCellClick?: (args: CellClickArgs<NoInfer<R>, NoInfer<SR>>, event: CellMouseEvent) => void;
  /** Function called whenever a cell is double clicked */
  onCellDoubleClick?: (args: CellClickArgs<NoInfer<R>, NoInfer<SR>>, event: CellMouseEvent) => void;
  /** Function called whenever a cell is right clicked */
  onCellContextMenu?: (args: CellClickArgs<NoInfer<R>, NoInfer<SR>>, event: CellMouseEvent) => void;
  onCellKeyDown?: (args: CellKeyDownArgs<NoInfer<R>, NoInfer<SR>>, event: CellKeyboardEvent) => void;
  /** Function called whenever cell selection is changed */
  onSelectedCellChange?: (args: CellSelectArgs<NoInfer<R>, NoInfer<SR>> | null) => void;
  /** Function called whenever the selected cell range changes. Requires `enableRangeSelection` */
  onSelectedRangeChange?: (range: CellRangeSelection | null) => void;
  /** Called when a column is resized */
  onColumnResize?: (idx: number, width: number) => void;
  /** Called when a column is reordered */
  onColumnsReorder?: (sourceColumnKey: string, targetColumnKey: string) => void;

  /**
   * Toggles and modes
   */
  /** @default true */
  enableVirtualization?: boolean;
  /**
   * Excel-like rectangular cell range selection: drag across cells, Shift+click or
   * Shift+Arrow keys to extend from the active cell.
   * @default false
   */
  enableRangeSelection?: boolean;

  renderRow?: (key: Key, props: RenderRowProps<R, SR>) => ReactNode;
  noRowsFallback?: ReactNode;

  /**
   * Miscellaneous
   */
  rowClass?: (row: NoInfer<R>, rowIdx: number) => string | undefined;
  'data-testid'?: string;
  deselectOnBlur?: boolean;
}

/**
 * Main API Component to render a data grid of rows and columns
 *
 * @example
 *
 * <DataGrid columns={columns} rows={rows} />
 */
function DataGrid<R, SR, K extends Key>(props: DataGridProps<R, SR, K>, ref: React.Ref<DataGridHandle>): ReactElement {
  const {
    // Grid and data Props
    columns: rawColumns,
    rows,
    topSummaryRows,
    bottomSummaryRows,
    rowKeyGetter,
    onRowsChange,
    // Dimensions props
    rowHeight: rawRowHeight,
    headerRowHeight: rawHeaderRowHeight,
    summaryRowHeight: rawSummaryRowHeight,
    // Feature props
    selectedRows,
    onSelectedRowsChange,
    sortColumns,
    onSortColumnsChange,
    defaultColumnOptions,
    presenceMarkers,
    // Event props
    onCellClick,
    onCellDoubleClick,
    onCellContextMenu,
    onCellKeyDown,
    onSelectedCellChange,
    onSelectedRangeChange,
    onScroll,
    onColumnResize,
    onColumnsReorder,
    onFill,
    copiedOutline = null,
    onClipboardCopy,
    onClipboardCut,
    onClipboardPaste,
    onCopiedOutlineClear,
    // Toggles and modes
    enableVirtualization: rawEnableVirtualization,
    enableRangeSelection = false,
    noRowsFallback,
    renderRow = defaultRenderRow,
    // Miscellaneous
    className,
    style,
    rowClass,
    // ARIA
    role: rawRole,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
    'aria-rowcount': rawAriaRowCount,
    'data-testid': testId,
    deselectOnBlur = false,
  } = props;

  /**
   * defaults
   */
  const role = rawRole ?? 'grid';
  const rowHeight = rawRowHeight ?? 35;
  const headerRowHeight = rawHeaderRowHeight ?? (typeof rowHeight === 'number' ? rowHeight : 35);
  const summaryRowHeight = rawSummaryRowHeight ?? (typeof rowHeight === 'number' ? rowHeight : 35);
  const enableVirtualization = rawEnableVirtualization ?? true;

  /**
   * refs
   */
  const gridRef = useRef<HTMLDivElement>(null);

  /**
   * states
   */
  const { scrollTop, scrollLeft } = useScrollState(gridRef);
  const [gridWidth, gridHeight, isGridMeasured] = useGridDimensions(gridRef);
  const [resizedColumnWidths, setResizedColumnWidths] = useState((): ReadonlyMap<string, number> => new Map());
  const [measuredColumnWidths, setMeasuredColumnWidths] = useState((): ReadonlyMap<string, number> => new Map());
  const [isDragging, setDragging] = useState(false);
  const [draggedOverRowIdx, setOverRowIdx] = useState<number | undefined>(undefined);

  const getColumnWidth = useCallback(
    (column: CalculatedColumn<R, SR>) => {
      return resizedColumnWidths.get(column.key) ?? measuredColumnWidths.get(column.key) ?? column.width;
    },
    [measuredColumnWidths, resizedColumnWidths],
  );

  const {
    columns,
    colSpanColumns,
    lastFrozenColumnIndex,
    headerRowsCount,
    colOverscanStartIdx,
    colOverscanEndIdx,
    templateColumns,
    layoutCssVars,
    totalFrozenColumnWidth,
  } = useCalculatedColumns({
    rawColumns,
    defaultColumnOptions,
    getColumnWidth,
    scrollLeft,
    viewportWidth: gridWidth,
    enableVirtualization,
  });

  const topSummaryRowsCount = topSummaryRows?.length ?? 0;
  const bottomSummaryRowsCount = bottomSummaryRows?.length ?? 0;
  const summaryRowsCount = topSummaryRowsCount + bottomSummaryRowsCount;
  const headerAndTopSummaryRowsCount = headerRowsCount + topSummaryRowsCount;
  const groupedColumnHeaderRowsCount = headerRowsCount - 1;
  const minRowIdx = -headerAndTopSummaryRowsCount;
  const mainHeaderRowIdx = minRowIdx + groupedColumnHeaderRowsCount;
  const maxRowIdx = rows.length + bottomSummaryRowsCount - 1;
  const frozenShadowStyles: React.CSSProperties = {
    gridColumnStart: lastFrozenColumnIndex + 2,
    insetInlineStart: totalFrozenColumnWidth,
  };
  const maxColIdx = columns.length - 1;

  /**
   * refs
   */
  const latestDraggedOverRowIdx = useRef(draggedOverRowIdx);
  const lastSelectedRowIdx = useRef(-1);

  /**
   * callbacks
   */
  const setDraggedOverRowIdx = useCallback((rowIdx?: number) => {
    setOverRowIdx(rowIdx);
    latestDraggedOverRowIdx.current = rowIdx;
  }, []);

  const {
    selectedPosition,
    setSelectedPosition,
    setPositionToFocus,
    selectedCellIsWithinSelectionBounds,
    selectedCellIsWithinViewportBounds,
    validatePosition,
    getSelectedColumn,
    getSelectedRow,
  } = useSelectedPosition<R, SR>({
    gridRef,
    columns,
    rows,
    maxColIdx,
    minRowIdx,
    maxRowIdx,
    setDraggedOverRowIdx,
  });
  const { scrollToPosition, setScrollToPosition, scrollToPositionElement } = useScrollToPosition({ gridRef });

  /**
   * computed values
   */
  const headerRowsHeight = headerRowsCount * headerRowHeight;
  const summaryRowsHeight = summaryRowsCount * summaryRowHeight;
  const clientHeight = gridHeight - headerRowsHeight - summaryRowsHeight;
  const viewportReady = isGridMeasured || !enableVirtualization;
  const isSelectable = selectedRows != null && onSelectedRowsChange != null;
  const ariaRowCount = rawAriaRowCount ?? headerRowsCount + rows.length + summaryRowsCount;

  const allRowsSelected = useMemo((): boolean => {
    // no rows to select = explicitely unchecked
    const { length } = rows;
    return (
      length !== 0 &&
      selectedRows != null &&
      rowKeyGetter != null &&
      selectedRows.size >= length &&
      rows.every((row) => selectedRows.has(rowKeyGetter(row)))
    );
  }, [rows, selectedRows, rowKeyGetter]);

  const presenceByRow = usePresenceIndex(presenceMarkers);

  const copiedRange = useMemo((): CellRangeSelection | null => {
    if (copiedOutline == null || rowKeyGetter == null) return null;
    return resolveCopiedRange(
      copiedOutline,
      rows.map((row) => String(rowKeyGetter(row))),
      columns.map((column) => column.key),
    );
  }, [copiedOutline, rowKeyGetter, rows, columns]);

  const {
    rowOverscanStartIdx,
    rowOverscanEndIdx,
    totalRowHeight,
    gridTemplateRows,
    getRowTop,
    getRowHeight,
    findRowIdx,
  } = useViewportRows({
    rows,
    rowHeight,
    clientHeight,
    scrollTop,
    enableVirtualization,
  });

  const viewportColumns = useViewportColumns({
    columns,
    colSpanColumns,
    colOverscanStartIdx,
    colOverscanEndIdx,
    lastFrozenColumnIndex,
    rowOverscanStartIdx,
    rowOverscanEndIdx,
    rows,
    topSummaryRows,
    bottomSummaryRows,
  });

  const { gridTemplateColumns, handleColumnResize } = useColumnWidths(
    columns,
    viewportColumns,
    templateColumns,
    gridRef,
    gridWidth,
    resizedColumnWidths,
    measuredColumnWidths,
    setResizedColumnWidths,
    setMeasuredColumnWidths,
    onColumnResize,
  );

  /**
   * The identity of the wrapper function is stable so it won't break memoization
   */
  const handleColumnResizeLatest = useLatestFunc(handleColumnResize);
  const onColumnsReorderLastest = useLatestFunc(onColumnsReorder);
  const onSortColumnsChangeLatest = useLatestFunc(onSortColumnsChange);
  const onCellClickLatest = useLatestFunc(onCellClick);
  const onCellDoubleClickLatest = useLatestFunc(onCellDoubleClick);
  const onCellContextMenuLatest = useLatestFunc(onCellContextMenu);
  const selectRowLatest = useLatestFunc(selectRow);
  const handleFormatterRowChangeLatest = useLatestFunc(updateRow);
  const selectCellLatest = useLatestFunc(selectCell);
  const selectHeaderCellLatest = useLatestFunc(({ idx, rowIdx }: Position) => {
    selectCell({ rowIdx: minRowIdx + rowIdx - 1, idx });
  });

  const {
    normalizedRange,
    isRangeDragging,
    collapseRange,
    selectRange,
    handleCellMouseDown,
    handleCellMouseEnter,
    handleClickCapture,
    handleShiftArrowKey,
  } = useRangeSelection<R>({
    enabled: enableRangeSelection,
    rows,
    maxColIdx,
    gridRef,
    selectedPosition,
    selectedCellIsWithinViewportBounds,
    selectCell: selectCellLatest,
    scrollToCell,
    onSelectedRangeChange,
  });

  useImperativeHandle(ref, () => ({
    element: gridRef.current,
    scrollToCell,
    selectCell,
    collapseRange,
  }));

  /**
   * event handlers
   */
  function selectRow(args: SelectRowEvent<R>): void {
    if (!onSelectedRowsChange) return;

    assertIsValidKeyGetter<R, K>(rowKeyGetter);

    if (args.type === 'HEADER') {
      const newSelectedRows = new Set(selectedRows);
      for (const row of rows) {
        const rowKey = rowKeyGetter(row);
        if (args.checked) {
          newSelectedRows.add(rowKey);
        } else {
          newSelectedRows.delete(rowKey);
        }
      }
      onSelectedRowsChange(newSelectedRows);
      return;
    }

    const { row, checked, isShiftClick } = args;
    const newSelectedRows = new Set(selectedRows);
    const rowKey = rowKeyGetter(row);
    if (checked) {
      newSelectedRows.add(rowKey);
      const previousRowIdx = lastSelectedRowIdx.current;
      const rowIdx = rows.indexOf(row);
      lastSelectedRowIdx.current = rowIdx;
      if (isShiftClick && previousRowIdx !== -1 && previousRowIdx !== rowIdx) {
        const step = sign(rowIdx - previousRowIdx);
        for (let i = previousRowIdx + step; i !== rowIdx; i += step) {
          const row = rows[i];
          newSelectedRows.add(rowKeyGetter(row));
        }
      }
    } else {
      newSelectedRows.delete(rowKey);
      lastSelectedRowIdx.current = -1;
    }

    onSelectedRowsChange(newSelectedRows);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
    const { idx, rowIdx, mode } = selectedPosition;
    if (mode === 'EDIT') return;

    if (onCellKeyDown && isRowIdxWithinViewportBounds(rowIdx)) {
      const row = rows[rowIdx];
      const cellEvent = createCellEvent(event);
      onCellKeyDown(
        {
          mode: 'SELECT',
          row,
          column: columns[idx],
          rowIdx,
          selectCell,
        },
        cellEvent,
      );
      if (cellEvent.isGridDefaultPrevented()) return;
    }
    if (!(event.target instanceof Element)) return;
    const isCellEvent = event.target.closest('.rdg-cell') !== null;
    if (!isCellEvent) return;

    if (
      event.shiftKey &&
      (event.key === 'ArrowUp' ||
        event.key === 'ArrowDown' ||
        event.key === 'ArrowLeft' ||
        event.key === 'ArrowRight') &&
      handleShiftArrowKey(event.key)
    ) {
      // extend the range head instead of navigating
      event.preventDefault();
      return;
    }

    switch (event.key) {
      case 'Escape':
        onCopiedOutlineClear?.();
        collapseRange();
        return;
      case 'ArrowUp':
      case 'ArrowDown':
      case 'ArrowLeft':
      case 'ArrowRight':
      case 'Tab':
      case 'Home':
      case 'End':
      case 'PageUp':
      case 'PageDown':
        navigate(event);
        break;
      default:
        handleCellInput(event);
        break;
    }
  }

  function updateRow(column: CalculatedColumn<R, SR>, rowIdx: number, row: R): void {
    if (typeof onRowsChange !== 'function') return;
    if (row === rows[rowIdx]) return;
    const updatedRows = [...rows];
    updatedRows[rowIdx] = row;
    onRowsChange(updatedRows, {
      indexes: [rowIdx],
      column,
    });
  }

  function commitEditorChanges(): void {
    if (selectedPosition.mode !== 'EDIT') return;
    updateRow(getSelectedColumn(), selectedPosition.rowIdx, selectedPosition.row);
  }

  function handleClipboardCopy(event: React.ClipboardEvent<HTMLDivElement>): void {
    handleClipboardCopyOrCut(onClipboardCopy, event);
  }

  function handleClipboardCut(event: React.ClipboardEvent<HTMLDivElement>): void {
    handleClipboardCopyOrCut(onClipboardCut, event);
  }

  function handleClipboardCopyOrCut(
    handler: DataGridProps<R, SR, K>['onClipboardCopy'],
    event: React.ClipboardEvent<HTMLDivElement>,
  ): void {
    // A range only makes sense anchored on a plain, in-bounds cell selection.
    // Otherwise (editor open, or the initial -1 / -Infinity position) emitting a
    // range built from those coordinates would hand the consumer an out-of-bounds
    // rectangle — let the native copy/cut proceed instead.
    if (handler == null || !selectedCellIsWithinViewportBounds || selectedPosition.mode === 'EDIT') return;
    // copy highlighted text only
    if (window.getSelection()?.isCollapsed === false) return;
    event.preventDefault();
    const range = normalizedRange ?? {
      startIdx: selectedPosition.idx,
      startRowIdx: selectedPosition.rowIdx,
      endIdx: selectedPosition.idx,
      endRowIdx: selectedPosition.rowIdx,
    };
    handler(
      {
        range,
        rows: rows.slice(range.startRowIdx, range.endRowIdx + 1),
        columns: columns.slice(range.startIdx, range.endIdx + 1),
      },
      event,
    );
  }

  function handleClipboardPaste(event: React.ClipboardEvent<HTMLDivElement>): void {
    // The grid only notifies — the consumer reads the clipboard flavors and owns
    // the mutation, sizing the target rectangle from the payload (top-left =
    // active cell). In edit mode the native paste must reach the editor input.
    if (onClipboardPaste == null || !selectedCellIsWithinViewportBounds || selectedPosition.mode === 'EDIT') return;
    event.preventDefault();
    const { idx, rowIdx } = selectedPosition;
    const pasted = onClipboardPaste({ target: { idx, rowIdx }, rows, columns }, event);
    // Excel-style: move the range selection over the pasted block. selectRange is
    // a no-op on a single cell (`getNormalizedRange` collapses it), so guarding
    // is just to skip needless re-anchoring.
    if (pasted && (pasted.endIdx > pasted.startIdx || pasted.endRowIdx > pasted.startRowIdx)) {
      selectRange?.(
        { idx: pasted.startIdx, rowIdx: pasted.startRowIdx },
        { idx: pasted.endIdx, rowIdx: pasted.endRowIdx },
      );
    }
    // Paste consumes the marching ants, not the buffer — the clipboard is
    // untouched, so repeated pastes keep working.
    onCopiedOutlineClear?.();
  }

  function handleCellInput(event: KeyboardEvent<HTMLDivElement>): void {
    if (!selectedCellIsWithinViewportBounds) return;
    const row = getSelectedRow();
    const { key, shiftKey } = event;

    // Select the row on Shift + Space
    if (isSelectable && shiftKey && key === ' ') {
      assertIsValidKeyGetter<R, K>(rowKeyGetter);
      const rowKey = rowKeyGetter(row);
      selectRow({ type: 'ROW', row, checked: !selectedRows.has(rowKey), isShiftClick: false });
      // do not scroll
      event.preventDefault();
      return;
    }

    // The paste chord must not open the editor when grid-level paste is wired —
    // the native paste event that follows this keydown handles it. keyCode keeps
    // this layout-independent, same reasoning as isDefaultCellInput's vKey check.
    if (onClipboardPaste != null && isCtrlKeyHeldDown(event) && event.keyCode === 86) {
      return;
    }

    if (isCellEditable(selectedPosition) && isDefaultCellInput(event)) {
      // Typing into a cell opens the editor — same as above, that consumes the
      // copy and the marching-ants must go away.
      onCopiedOutlineClear?.();
      setSelectedPosition(({ idx, rowIdx }) => ({
        idx,
        rowIdx,
        mode: 'EDIT',
        row,
        originalRow: row,
      }));
      // We don't want event in editor
      if (key === 'Enter') {
        event.preventDefault();
      }
    }
  }

  /**
   * utils
   */
  function isRowIdxWithinViewportBounds(rowIdx: number): boolean {
    return rowIdx >= 0 && rowIdx < rows.length;
  }

  function scrollToCell({ idx, rowIdx }: PartialPosition): void {
    const scrollToIdx = idx !== undefined && idx > lastFrozenColumnIndex && idx < columns.length ? idx : undefined;
    const scrollToRowIdx =
      rowIdx !== undefined && isRowIdxWithinViewportBounds(rowIdx) ? rowIdx + headerAndTopSummaryRowsCount : undefined;

    if (scrollToIdx !== undefined || scrollToRowIdx !== undefined) {
      setScrollToPosition({ idx: scrollToIdx, rowIdx: scrollToRowIdx });
    }
  }

  function isCellEditable(position: Position): boolean {
    return (
      validatePosition(position).isCellInViewportBounds &&
      isSelectedCellEditable({ columns, rows, selectedPosition: position })
    );
  }

  function selectCell(position: Position | null, enableEditor?: boolean, scrollToCell = true): void {
    // any selection change re-anchors, so an existing range must collapse
    collapseRange();
    if (position === null) {
      setSelectedPosition(initialSelectedPosition);
      onSelectedCellChange?.(null);
      return;
    }
    if (!validatePosition(position).isCellInSelectionBounds) return;
    commitEditorChanges();

    const row = rows[position.rowIdx];
    const samePosition = isSamePosition(selectedPosition, position);

    if (enableEditor && isCellEditable(position)) {
      // Editing consumes the copy — Excel/Sheets drop the marching-ants the
      // moment you start typing into a cell.
      onCopiedOutlineClear?.();
      setSelectedPosition({ ...position, mode: 'EDIT', row, originalRow: row });
    } else if (samePosition && scrollToCell) {
      // Avoid re-renders if the selected cell state is the same
      scrollIntoView(getCellToScroll(gridRef.current!));
    } else {
      const newPosition: SelectCellState = { ...position, mode: 'SELECT' };
      setSelectedPosition(newPosition);
      if (scrollToCell) {
        setPositionToFocus(newPosition);
      }
    }

    if (onSelectedCellChange && !samePosition) {
      onSelectedCellChange({
        rowIdx: position.rowIdx,
        row,
        column: columns[position.idx],
      });
    }
  }

  function getNextPosition(key: string, ctrlKey: boolean, shiftKey: boolean): Position {
    const { idx, rowIdx } = selectedPosition;
    const isRowSelected = selectedCellIsWithinSelectionBounds && idx === -1;

    switch (key) {
      case 'ArrowUp':
        return { idx, rowIdx: rowIdx - 1 };
      case 'ArrowDown':
        return { idx, rowIdx: rowIdx + 1 };
      case 'ArrowLeft':
        return { idx: idx - 1, rowIdx };
      case 'ArrowRight':
        return { idx: idx + 1, rowIdx };
      case 'Tab':
        return { idx: idx + (shiftKey ? -1 : 1), rowIdx };
      case 'Home':
        // If row is selected then move focus to the first row
        if (isRowSelected) return { idx, rowIdx: minRowIdx };
        return { idx: 0, rowIdx: ctrlKey ? minRowIdx : rowIdx };
      case 'End':
        // If row is selected then move focus to the last row.
        if (isRowSelected) return { idx, rowIdx: maxRowIdx };
        return { idx: maxColIdx, rowIdx: ctrlKey ? maxRowIdx : rowIdx };
      case 'PageUp': {
        if (selectedPosition.rowIdx === minRowIdx) return selectedPosition;
        const nextRowY = getRowTop(rowIdx) + getRowHeight(rowIdx) - clientHeight;
        return { idx, rowIdx: nextRowY > 0 ? findRowIdx(nextRowY) : 0 };
      }
      case 'PageDown': {
        if (selectedPosition.rowIdx >= rows.length) return selectedPosition;
        const nextRowY = getRowTop(rowIdx) + clientHeight;
        return { idx, rowIdx: nextRowY < totalRowHeight ? findRowIdx(nextRowY) : rows.length - 1 };
      }
      default:
        return selectedPosition;
    }
  }

  function navigate(event: KeyboardEvent<HTMLDivElement>): void {
    const { key, shiftKey } = event;
    let cellNavigationMode: CellNavigationMode = 'NONE';
    if (key === 'Tab') {
      if (
        canExitGrid({
          shiftKey,
          maxColIdx,
          minRowIdx,
          maxRowIdx,
          selectedPosition,
        })
      ) {
        commitEditorChanges();
        // Allow focus to leave the grid so the next control in the tab order can be focused
        return;
      }

      cellNavigationMode = 'CHANGE_ROW';
    }

    // Do not allow focus to leave and prevent scrolling
    event.preventDefault();

    const ctrlKey = isCtrlKeyHeldDown(event);
    const nextPosition = getNextPosition(key, ctrlKey, shiftKey);
    if (isSamePosition(selectedPosition, nextPosition)) return;

    const nextSelectedCellPosition = getNextSelectedCellPosition({
      moveUp: key === 'ArrowUp',
      moveNext: key === 'ArrowRight' || (key === 'Tab' && !shiftKey),
      columns,
      colSpanColumns,
      rows,
      topSummaryRows,
      bottomSummaryRows,
      minRowIdx,
      mainHeaderRowIdx,
      maxRowIdx,
      lastFrozenColumnIndex,
      cellNavigationMode,
      currentPosition: selectedPosition,
      nextPosition,
      isCellWithinBounds: (position: Position) => validatePosition(position).isCellInSelectionBounds,
    });

    selectCell(nextSelectedCellPosition);
  }

  function getDraggedOverCellIdx(currentRowIdx: number): number | undefined {
    if (draggedOverRowIdx === undefined) return;
    const { rowIdx } = selectedPosition;

    const isDraggedOver =
      rowIdx < draggedOverRowIdx
        ? rowIdx < currentRowIdx && currentRowIdx <= draggedOverRowIdx
        : rowIdx > currentRowIdx && currentRowIdx >= draggedOverRowIdx;

    return isDraggedOver ? selectedPosition.idx : undefined;
  }

  function handleBlur(event: React.FocusEvent<HTMLDivElement>): void {
    if (!deselectOnBlur) {
      return;
    }

    if (selectedPosition.mode === 'EDIT' || event.currentTarget.contains(event.relatedTarget)) {
      return;
    }
    // Focus moving into a dialog comes back on close (returnFocus) — deselecting
    // now would hand it a cell whose roving tabindex has already dropped to -1.
    if (isInFloatingLayer(event.relatedTarget)) {
      return;
    }
    // The grid is dismissing its selection — a lingering copy outline with no
    // selection to anchor it would be misleading, so drop the ants too.
    onCopiedOutlineClear?.();
    selectCell(null);
  }

  function renderDragHandle(): ReactElement | undefined {
    if (onFill == null || selectedPosition.mode !== 'SELECT' || !selectedCellIsWithinViewportBounds) {
      return;
    }

    const { rowIdx } = selectedPosition;
    const column = getSelectedColumn();
    if (!isCellEditable(selectedPosition)) {
      return;
    }

    const columnWidth = getColumnWidth(column);

    return (
      <DragHandle
        gridRowStart={headerAndTopSummaryRowsCount + rowIdx + 1}
        rows={rows}
        column={column}
        columnWidth={columnWidth}
        maxColIdx={maxColIdx}
        isLastRow={rowIdx === maxRowIdx}
        selectedPosition={selectedPosition}
        isCellEditable={isCellEditable}
        latestDraggedOverRowIdx={latestDraggedOverRowIdx}
        onRowsChange={onRowsChange}
        onClick={() => focusCellOrCellContent(gridRef.current!)}
        onFill={onFill}
        setDragging={setDragging}
        setDraggedOverRowIdx={setDraggedOverRowIdx}
      />
    );
  }

  function getCellEditor(rowIdx: number): ReactElement | undefined {
    if (!selectedCellIsWithinViewportBounds || selectedPosition.rowIdx !== rowIdx || selectedPosition.mode !== 'EDIT') {
      return;
    }

    const { row } = selectedPosition;
    const column = getSelectedColumn();
    const colSpan = getColSpan(column, lastFrozenColumnIndex, { type: 'ROW', row });

    function closeEditor(shouldFocusCell: boolean): void {
      const newPosition: SelectCellState = { idx: selectedPosition.idx, rowIdx, mode: 'SELECT' };
      setSelectedPosition(newPosition);
      if (shouldFocusCell) {
        setPositionToFocus(newPosition);
      }
    }

    function onRowChange(row: R, commitChanges: boolean, shouldFocusCell: boolean, shouldKeepOpen: boolean): void {
      if (commitChanges) {
        // Prevents two issues when editor is closed by clicking on a different cell
        //
        // Otherwise commitEditorChanges may be called before the cell state is changed to
        // SELECT and this results in onRowChange getting called twice.
        flushSync(() => {
          updateRow(column, selectedPosition.rowIdx, row);
          !shouldKeepOpen && closeEditor(shouldFocusCell);
        });
      } else {
        setSelectedPosition((position) => ({ ...position, row }));
      }
    }

    return (
      <EditCell
        key={column.key}
        column={column}
        colSpan={colSpan}
        row={row}
        rowIdx={rowIdx}
        onRowChange={onRowChange}
        closeEditor={closeEditor}
        onKeyDown={onCellKeyDown}
        navigate={navigate}
      />
    );
  }

  function getRowViewportColumns(rowIdx: number): readonly CalculatedColumn<R, SR>[] {
    // idx can be -1 if grouping is enabled
    const selectedColumn = selectedPosition.idx === -1 ? undefined : columns[selectedPosition.idx];
    if (
      selectedColumn !== undefined &&
      selectedPosition.rowIdx === rowIdx &&
      !viewportColumns.includes(selectedColumn)
    ) {
      // Add the selected column to viewport columns if the cell is not within the viewport
      return selectedPosition.idx > colOverscanEndIdx
        ? [...viewportColumns, selectedColumn]
        : [
            ...viewportColumns.slice(0, lastFrozenColumnIndex + 1),
            selectedColumn,
            ...viewportColumns.slice(lastFrozenColumnIndex + 1),
          ];
    }
    return viewportColumns;
  }

  function getViewportRows(): ReactNode[] {
    const rowElements: ReactNode[] = [];

    const { idx: selectedIdx, rowIdx: selectedRowIdx } = selectedPosition;

    const startRowIdx =
      selectedCellIsWithinViewportBounds && selectedRowIdx < rowOverscanStartIdx
        ? rowOverscanStartIdx - 1
        : rowOverscanStartIdx;
    const endRowIdx =
      selectedCellIsWithinViewportBounds && selectedRowIdx > rowOverscanEndIdx
        ? rowOverscanEndIdx + 1
        : rowOverscanEndIdx;

    for (let viewportRowIdx = startRowIdx; viewportRowIdx <= endRowIdx; viewportRowIdx++) {
      const isRowOutsideViewport =
        viewportRowIdx === rowOverscanStartIdx - 1 || viewportRowIdx === rowOverscanEndIdx + 1;
      const rowIdx = isRowOutsideViewport ? selectedRowIdx : viewportRowIdx;

      let rowColumns = viewportColumns;
      const selectedColumn = selectedIdx === -1 ? undefined : columns[selectedIdx];
      if (selectedColumn !== undefined) {
        if (isRowOutsideViewport) {
          // if the row is outside the viewport then only render the selected cell
          rowColumns = [selectedColumn];
        } else {
          // if the row is within the viewport and cell is not, add the selected column to viewport columns
          rowColumns = getRowViewportColumns(rowIdx);
        }
      }

      const row = rows[rowIdx];
      const rangeSlice =
        normalizedRange !== null && rowIdx >= normalizedRange.startRowIdx && rowIdx <= normalizedRange.endRowIdx
          ? {
              startIdx: normalizedRange.startIdx,
              endIdx: normalizedRange.endIdx,
              isTopRow: rowIdx === normalizedRange.startRowIdx,
              isBottomRow: rowIdx === normalizedRange.endRowIdx,
            }
          : null;
      const copiedSlice =
        copiedRange !== null && rowIdx >= copiedRange.startRowIdx && rowIdx <= copiedRange.endRowIdx
          ? {
              startIdx: copiedRange.startIdx,
              endIdx: copiedRange.endIdx,
              isTopRow: rowIdx === copiedRange.startRowIdx,
              isBottomRow: rowIdx === copiedRange.endRowIdx,
            }
          : null;
      const gridRowStart = headerAndTopSummaryRowsCount + rowIdx + 1;
      let key: K | number = rowIdx;
      let isRowSelected = false;
      if (typeof rowKeyGetter === 'function') {
        key = rowKeyGetter(row);
        isRowSelected = selectedRows?.has(key) ?? false;
      }

      rowElements.push(
        renderRow(key, {
          // aria-rowindex is 1 based
          'aria-rowindex': headerAndTopSummaryRowsCount + rowIdx + 1,
          'aria-selected': isSelectable ? isRowSelected : undefined,
          rowIdx,
          row,
          viewportColumns: rowColumns,
          isRowSelected,
          onCellClick: onCellClickLatest,
          onCellDoubleClick: onCellDoubleClickLatest,
          onCellContextMenu: onCellContextMenuLatest,
          rowClass,
          gridRowStart,
          height: getRowHeight(rowIdx),
          copiedSlice,
          selectedCellIdx: selectedRowIdx === rowIdx ? selectedIdx : undefined,
          draggedOverCellIdx: getDraggedOverCellIdx(rowIdx),
          rangeSlice,
          onCellMouseDown: handleCellMouseDown,
          onCellMouseEnter: handleCellMouseEnter,
          rowPresence: presenceByRow?.get(key as string),
          setDraggedOverRowIdx: isDragging ? setDraggedOverRowIdx : undefined,
          lastFrozenColumnIndex,
          onRowChange: handleFormatterRowChangeLatest,
          selectCell: selectCellLatest,
          selectedCellEditor: getCellEditor(rowIdx),
        }),
      );
    }

    return rowElements;
  }

  let templateRows = `repeat(${headerRowsCount}, ${headerRowHeight}px)`;
  if (topSummaryRowsCount > 0) {
    templateRows += ` repeat(${topSummaryRowsCount}, ${summaryRowHeight}px)`;
  }
  if (rows.length > 0) {
    templateRows += gridTemplateRows;
  }
  if (bottomSummaryRowsCount > 0) {
    templateRows += ` repeat(${bottomSummaryRowsCount}, ${summaryRowHeight}px)`;
  }

  return (
    <div
      role={role}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
      aria-multiselectable={isSelectable ? true : undefined}
      aria-colcount={columns.length}
      aria-rowcount={ariaRowCount}
      className={clsx(
        rootClassname,
        {
          [viewportDraggingClassname]: isDragging,
          [rangeSelectingClassname]: isRangeDragging,
        },
        className,
      )}
      style={
        {
          ...style,
          // set scrollPadding to correctly position non-sticky cells after scrolling
          scrollPaddingInlineStart:
            selectedPosition.idx > lastFrozenColumnIndex || scrollToPosition?.idx !== undefined
              ? `${totalFrozenColumnWidth}px`
              : undefined,
          scrollPaddingBlock:
            isRowIdxWithinViewportBounds(selectedPosition.rowIdx) || scrollToPosition?.rowIdx !== undefined
              ? `${headerRowsHeight + topSummaryRowsCount * summaryRowHeight}px ${
                  bottomSummaryRowsCount * summaryRowHeight
                }px`
              : undefined,
          gridTemplateColumns,
          gridTemplateRows: templateRows,
          '--rdg-header-row-height': `${headerRowHeight}px`,
          '--rdg-summary-row-height': `${summaryRowHeight}px`,
          ...layoutCssVars,
        } as unknown as React.CSSProperties
      }
      ref={gridRef}
      onScroll={onScroll}
      onKeyDown={handleKeyDown}
      onCopy={handleClipboardCopy}
      onCut={handleClipboardCut}
      onPaste={handleClipboardPaste}
      onClickCapture={handleClickCapture}
      onBlur={handleBlur}
      data-testid={testId}
    >
      <Tooltip
        id={gridTooltipId}
        className={tooltipContainerClassname}
        noArrow
        offset={2}
        render={({ content, activeAnchor }) =>
          // Only render when there's content — react-tooltip treats any truthy
          // render output as "has content" and would otherwise show an empty box.
          content ? (
            <div
              className={tooltipSwatchClassname}
              style={{ backgroundColor: activeAnchor?.getAttribute('data-presence-color') ?? undefined }}
            >
              {content}
            </div>
          ) : null
        }
      />
      <RowSelectionChangeProvider value={selectRowLatest}>
        <RowSelectionProvider value={allRowsSelected}>
          {Array.from({ length: groupedColumnHeaderRowsCount }, (_, index) => (
            <GroupedColumnHeaderRow
              key={index}
              rowIdx={index + 1}
              level={-groupedColumnHeaderRowsCount + index}
              columns={getRowViewportColumns(minRowIdx + index)}
              selectedCellIdx={selectedPosition.rowIdx === minRowIdx + index ? selectedPosition.idx : undefined}
              selectCell={selectHeaderCellLatest}
            />
          ))}
          <HeaderRow
            rowIdx={headerRowsCount}
            columns={getRowViewportColumns(mainHeaderRowIdx)}
            onColumnResize={handleColumnResizeLatest}
            onColumnsReorder={onColumnsReorderLastest}
            sortColumns={sortColumns}
            onSortColumnsChange={onSortColumnsChangeLatest}
            lastFrozenColumnIndex={lastFrozenColumnIndex}
            selectedCellIdx={selectedPosition.rowIdx === mainHeaderRowIdx ? selectedPosition.idx : undefined}
            selectCell={selectHeaderCellLatest}
            shouldFocusGrid={!selectedCellIsWithinSelectionBounds}
          />
        </RowSelectionProvider>
        {rows.length === 0 && noRowsFallback ? (
          noRowsFallback
        ) : (
          <>
            {topSummaryRows?.map((row, rowIdx) => {
              const gridRowStart = headerRowsCount + 1 + rowIdx;
              const summaryRowIdx = mainHeaderRowIdx + 1 + rowIdx;
              const isSummaryRowSelected = selectedPosition.rowIdx === summaryRowIdx;
              const top = headerRowsHeight + summaryRowHeight * rowIdx;

              return (
                <SummaryRow
                  key={rowIdx}
                  aria-rowindex={gridRowStart}
                  rowIdx={summaryRowIdx}
                  gridRowStart={gridRowStart}
                  row={row}
                  top={top}
                  bottom={undefined}
                  viewportColumns={getRowViewportColumns(summaryRowIdx)}
                  lastFrozenColumnIndex={lastFrozenColumnIndex}
                  selectedCellIdx={isSummaryRowSelected ? selectedPosition.idx : undefined}
                  isTop
                  selectCell={selectCellLatest}
                />
              );
            })}
            {viewportReady ? getViewportRows() : null}
            {bottomSummaryRows?.map((row, rowIdx) => {
              const gridRowStart = headerAndTopSummaryRowsCount + rows.length + rowIdx + 1;
              const summaryRowIdx = rows.length + rowIdx;
              const isSummaryRowSelected = selectedPosition.rowIdx === summaryRowIdx;
              const top =
                clientHeight > totalRowHeight
                  ? gridHeight - summaryRowHeight * (bottomSummaryRowsCount - rowIdx)
                  : undefined;
              const bottom = top === undefined ? summaryRowHeight * (bottomSummaryRowsCount - 1 - rowIdx) : undefined;

              return (
                <SummaryRow
                  aria-rowindex={ariaRowCount - bottomSummaryRowsCount + rowIdx + 1}
                  key={rowIdx}
                  rowIdx={summaryRowIdx}
                  gridRowStart={gridRowStart}
                  row={row}
                  top={top}
                  bottom={bottom}
                  viewportColumns={getRowViewportColumns(summaryRowIdx)}
                  lastFrozenColumnIndex={lastFrozenColumnIndex}
                  selectedCellIdx={isSummaryRowSelected ? selectedPosition.idx : undefined}
                  isTop={false}
                  selectCell={selectCellLatest}
                />
              );
            })}
          </>
        )}
      </RowSelectionChangeProvider>

      {lastFrozenColumnIndex > -1 && (
        <>
          <div
            className={frozenColumnShadowTopClassname}
            style={{
              ...frozenShadowStyles,
              gridRowStart: 1,
              gridRowEnd: headerRowsCount + 1 + topSummaryRowsCount,
              insetBlockStart: 0,
            }}
          />

          {rows.length > 0 && (
            <div
              className={frozenColumnShadowClassname}
              style={{
                ...frozenShadowStyles,
                gridRowStart: headerAndTopSummaryRowsCount + rowOverscanStartIdx + 1,
                gridRowEnd: headerAndTopSummaryRowsCount + rowOverscanEndIdx + 2,
              }}
            />
          )}

          {bottomSummaryRows != null && bottomSummaryRowsCount > 0 && (
            <div
              className={frozenColumnShadowTopClassname}
              style={{
                ...frozenShadowStyles,
                gridRowStart: headerAndTopSummaryRowsCount + rows.length + 1,
                gridRowEnd: headerAndTopSummaryRowsCount + rows.length + 1 + bottomSummaryRowsCount,
                insetBlockStart:
                  clientHeight > totalRowHeight ? gridHeight - summaryRowHeight * bottomSummaryRowsCount : undefined,
                insetBlockEnd: clientHeight > totalRowHeight ? undefined : 0,
              }}
            />
          )}
        </>
      )}

      {renderDragHandle()}

      {/* render empty cells that span only 1 column so we can safely measure column widths, regardless of colSpan */}
      {renderMeasuringCells(viewportColumns)}

      {scrollToPositionElement}
    </div>
  );
}

function isSamePosition(p1: Position, p2: Position): boolean {
  return p1.idx === p2.idx && p1.rowIdx === p2.rowIdx;
}

export default forwardRef(DataGrid) as <R, SR = unknown, K extends Key = Key>(
  props: DataGridProps<R, SR, K> & RefAttributes<DataGridHandle>,
) => JSX.Element;
